import React from "react";

import { Box } from "@UI/Components/Box/Box";
import { Button } from "@UI/Components/Button/Button";
import { VStack } from "@UI/Components/Stack/Stack";
import { RouterLink } from "@Libs/Router/Components/RouterLink/RouterLink";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import useI18n from "@I18n/hooks/useI18n";
import useForm from "@Libs/Utils/hooks/useForm";
import { useCreateUserSession } from "@modules/Authentication/hooks/useEmailLogin";
import { getUserSessionError } from "@modules/Authentication/services/emailLogin";

import { AuthFormControl } from "../../Components/AuthFormControl/AuthFormControl";
import { authSubmitStyles } from "../../Components/AuthFormControl/authStyles";
import { PasswordInput } from "../../Components/PasswordInput/PasswordInput";
import { TaskCreationOutcomeModals } from "../../Components/TaskCreationOutcomeModals/TaskCreationOutcomeModals";
import { AUTH_SCREEN_KEYS, PASSWORD_MIN_LENGTH } from "../../config/constants";
import { useAuthStepCompletion } from "../../hooks/useAuthStepCompletion";
import { useRequireAuthIdentity } from "../../hooks/useRequireAuthIdentity";
import messages from "../../messages";
import { AuthStepLayout } from "../AuthStepLayout/AuthStepLayout";

const PASSWORD_FIELD_ID = "taskCreationAuthPassword";
/** Fixed so a repeated failure replaces its toast instead of stacking. */
const LOGIN_FAILED_TOAST_ID = "taskCreation.auth.login.failed";

interface IPasswordFormValues {
  password: string;
}

/**
 * The password for an address that already has an account. On success the flow
 * ends here: the claim is authenticated by the bearer `createUserSession` just
 * stored, and the success modal takes it from there — the same ending a
 * logged-in user gets on the last config screen.
 */
export const AuthLoginScreen = () => {
  const { formatMessage } = useI18n();

  const identity = useRequireAuthIdentity();

  const { mutateAsync: createSession } = useCreateUserSession();
  const {
    showAccountError,
    publishAndFinish,
    isPublishing,
    publishedRequestId,
  } = useAuthStepCompletion(LOGIN_FAILED_TOAST_ID, AUTH_SCREEN_KEYS.LOGIN);

  const {
    register,
    handleSubmit,
    setError,
    // Both halves of Continue, so the button cannot go idle in between.
    formState: { errors, isSubmitting },
  } = useForm<IPasswordFormValues>({ defaultValues: { password: "" } });

  const onSubmit = async ({ password }: IPasswordFormValues) => {
    if (!identity) return;

    try {
      await createSession({ email: identity.email, password });
    } catch (error) {
      const code = getUserSessionError(error);

      // The only refusal the password itself can answer. It names the email
      // too: the endpoint returns this for an unknown address as well, and
      // saying "wrong password" would confirm the address has an account.
      if (code === "unauthorized") {
        setError("password", {
          message: formatMessage(
            messages["taskCreation.auth.error.invalidCredentials"]
          ),
        });

        return;
      }

      // Every other refusal is the same to the visitor: nothing on this form
      // can answer it, and the endpoint's remaining codes are about the
      // account, not the password.
      showAccountError(messages["taskCreation.error.saveFailed"]);

      return;
    }

    await publishAndFinish();
  };

  if (!identity) return null;

  return (
    <AuthStepLayout
      title={
        <FormattedMessage
          {...messages["taskCreation.auth.login.title"]}
          // `User` validates a first name on every save, so the lookup only
          // comes back without one for a legacy row. Defaulted rather than
          // branched: an absent value is what react-intl throws on.
          values={{ firstName: identity.firstName ?? "" }}
        />
      }
      subtitle={identity.email}
    >
      <TaskCreationOutcomeModals
        isPublishing={isPublishing}
        publishedRequestId={publishedRequestId}
      />
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <VStack alignItems="stretch" spacing="16px">
          <AuthFormControl
            htmlFor={PASSWORD_FIELD_ID}
            label={
              <FormattedMessage
                {...messages["taskCreation.auth.password.label"]}
              />
            }
            errorMessage={errors.password?.message}
          >
            {/* Focused on arrival — they are already typing, and this is the
                only field. Not done on the email screen, where a raised
                keyboard would bury the selling line. */}
            <PasswordInput
              id={PASSWORD_FIELD_ID}
              autoFocus
              autoComplete="current-password"
              isDisabled={isSubmitting}
              {...register("password", {
                required: formatMessage(
                  messages["taskCreation.auth.password.required"]
                ),
                minLength: {
                  value: PASSWORD_MIN_LENGTH,
                  message: formatMessage(
                    messages["taskCreation.validation.minLength"],
                    { min: PASSWORD_MIN_LENGTH }
                  ),
                },
              })}
            />
          </AuthFormControl>
          <Button
            type="submit"
            isLoading={isSubmitting}
            isDisabled={isSubmitting}
            {...authSubmitStyles}
          >
            <FormattedMessage {...messages["taskCreation.cta.continue"]} />
          </Button>
          <Box>
            {/* Rails-rendered, so a real load; the draft survives it. */}
            <RouterLink
              route="RESET_PASSWORD"
              colorScheme="pink"
              color="pink.500"
              fontFamily="body"
              fontSize="lg"
              fontWeight="400"
              lineHeight="150%"
              textDecoration="underline"
              reloadDocument
            >
              <FormattedMessage
                {...messages["taskCreation.auth.password.forgotten"]}
              />
            </RouterLink>
          </Box>
        </VStack>
      </form>
    </AuthStepLayout>
  );
};
